import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  Spinner,
  UncontrolledTooltip,
  Dropdown,           // Import Dropdown
  DropdownToggle,     // Import DropdownToggle
  DropdownMenu,       // Import DropdownMenu
  DropdownItem        // Import DropdownItem
} from "reactstrap";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";

//Import Scrollbar
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import MarkdownRenderer from "./MarkdownRenderer"; // Assuming this is a custom component
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux"; // Import useDispatch
import { updateChatFeedbackStatus } from "../../slices/aiChatReport/thunk"; // Import the thunk

const ChatHistoryAIChatModal = ({ isOpen, toggle, session = {}, messages = [], loading = false, ReportedLegalIssues = false  }) => {
  const [messageBox, setMessageBox] = useState(null);
  const { t } = useTranslation();
  const [copied, setCopied] = useState({}); // State to track copied status
  // State to manage dropdown open/close for each message
  const [dropdownOpen, setDropdownOpen] = useState({});
  
  const dispatch = useDispatch(); // Initialize useDispatch
  const { updateChatFeedbackStatusLoading: StatusLoading } = useSelector((state) => state.AiChatReport || {});  

  // State to track which message is currently being updated
  const [updatingMessageId, setUpdatingMessageId] = useState(null);

  // Helper function to detect if text contains Arabic
  const containsArabic = (text) => {
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  const onImageError = (e) => {
    e.target.src = 'https://storage.googleapis.com/yasa-admin_images/MaleDefaultImg.png'; // Path to your default avatar
  };

  const handleCopy = (messageId, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({ [messageId]: true });
      setTimeout(() => setCopied(prev => ({ ...prev, [messageId]: false })), 2000);
    });
  };

  // Function to toggle dropdown for a specific message ID
  const toggleDropdown = (messageId) => {
    setDropdownOpen(prevState => ({
      ...prevState,
      [messageId]: !prevState[messageId]
    }));
  };

  const closeDropdown = (messageId) => {
    setDropdownOpen(prevState => ({
      ...prevState,
      [messageId]: false
    }));
  };

  // Function to handle status change (placeholder for actual API call)
  const handleStatusChange = (messageId, newStatus) => {
    setUpdatingMessageId(messageId);
    dispatch(updateChatFeedbackStatus({ ChatID: messageId, Status: newStatus }));
    toggleDropdown(messageId); // Close dropdown after selection
  };

  const getStatusDisplay = (message) => {
    switch (message.Status) {
      case 1: return { icon: "ri-time-line text-warning", text: t('Pending') };
      case 3: return { icon: "ri-check-double-line text-success", text: t('Completed') };
      case 2: return { icon: "ri-pause-line text-info", text: t('On Hold') };
      default: return { icon: "ri-feedback-line text-muted", text: t('No Feedback') };
    }
  };

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader
        id="myExtraLargeModalLabel"
        toggle={toggle}
      >
        {t("ChatHistoryFor")} {session?.AgentName || session?.UserName}
      </ModalHeader>
      <Container fluid className="px-0" style={{ height: 'calc(80vh - 120px)', overflowY: 'hidden' }}>
        <div className="chat-wrapper d-lg-flex gap-1 p-0" style={{ height: '100%' }}>
          <div className="user-chat w-100 overflow-hidden">
            <div className="chat-content d-lg-flex flex-column" style={{ height: '100%' }}>
              <div className="w-100 overflow-hidden position-relative flex-grow-1 d-flex flex-column">
                <div className="position-relative d-flex flex-column" style={{ height: '100%' }}>
                  <div className="p-3 user-chat-topbar border-bottom bg-light">
                    <Row className="align-items-center">
                      <Col sm={12}>
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 chat-user-img online user-own-img align-self-center me-3">
                            <img
                              src={session.AgentProfilePic || session.ProfilePictureUrl}
                              onError={onImageError}
                              className="rounded-circle avatar-xs"
                              alt=""
                            />
                            <span className={"user-status"}></span>
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <h5 className="text-truncate mb-0 fs-16">
                              <a
                                className="text-reset username"
                                data-bs-toggle="offcanvas"
                                href="#userProfileCanvasExample"
                                aria-controls="userProfileCanvasExample"
                              >
                                {`${session?.AgentName || session?.UserName}  (${session.SessionID})`}
                              </a>
                            </h5>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="position-relative flex-grow-1" id="users-chat" style={{ overflowY: 'auto' }}>
                    <PerfectScrollbar
                      className="chat-conversation p-3"
                      id="chat-conversation"
                      containerRef={ref => setMessageBox(ref)}
                      style={{ maxHeight: '100%', height: 'auto' }}>
                      <div id="elmLoader"></div>
                      <ul
                        className="list-unstyled chat-conversation-list"
                        id="users-conversation"
                      >

                        {loading ? <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Spinner style={{
                            height: '3rem',
                            width: '3rem',
                          }} className='me-2'> Loading... </Spinner>
                        </div> :
                          messages.map((message, key) => {
                            const statusDisplay = getStatusDisplay(message);

                            let likeStatus = null;
                            if (message.IsLiked === 1) {
                              likeStatus = { icon: "ri-thumb-up-fill text-success", text: t('Liked') };
                            } else if (message.IsLiked === 2) {
                              likeStatus = { icon: "ri-thumb-down-fill text-danger", text: t('Disliked') };
                            }
                            else {
                              likeStatus = { icon: "ri-thumb-up-line text-muted", text: t('No Like/Dislike') };
                            }

                            return (
                              <li
                                className={
                                  message.IsReceived
                                    ? " chat-list left"
                                    : "chat-list right"
                                }
                                key={key}
                              >
                                <div className="conversation-list">
                                  {message.IsReceived && (
                                    <div className="chat-avatar">
                                      <img
                                        src={session.AgentProfilePic || session.ProfilePictureUrl}
                                        onError={onImageError}
                                        alt=""
                                      />
                                    </div>
                                  )}

                                  <div className="user-chat-content">
                                    <div className="ctext-wrap">
                                      {message?.MessageType == 3 && message.Message ?
                                        <div className="message-img mb-0">
                                          <div className="message-img-list">
                                            <div
                                              style={{
                                                height: "100%",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                              }}>
                                              <iframe
                                                src={message.Message || ''}
                                                width="100%"
                                                height="300px"
                                                title="PDF Viewer"
                                                style={{ minHeight: "250px" }}
                                              >
                                              </iframe>

                                            </div>
                                          </div>
                                        </div> :
                                        message?.MessageType == 2 && message.Message ?
                                          <div className="message-img mb-0">
                                            <div className="message-img-list">
                                              <div>
                                                <a
                                                  className="popup-img d-inline-block"
                                                  href={message?.Message}
                                                  target="_blank"
                                                >
                                                  <img
                                                    src={message?.Message}
                                                    alt=""
                                                    className="rounded border"
                                                    style={{ maxWidth: '200px', height: 'auto' }}
                                                  />
                                                </a>
                                              </div>
                                            </div>
                                          </div> :
                                          <div className={ReportedLegalIssues && message.IsReceived ? 'ctext-wrap-content bg-danger-400' : session.ID == message.ID ? 'ctext-wrap-content bg-danger-400' : 'ctext-wrap-content'}>
                                            {message.IsReceived ? (
                                              <div
                                                className="mb-0 ctext-content"
                                                style={{
                                                  direction: containsArabic(message.Message) ? 'rtl' : 'ltr',
                                                  textAlign: containsArabic(message.Message) ? 'right' : 'left'
                                                }}
                                              >
                                                <MarkdownRenderer markdown={message.Message} />

                                              </div>
                                            ) : (
                                              <p
                                                className="mb-0 ctext-content"
                                                style={{
                                                  direction: containsArabic(message.Message) ? 'rtl' : 'ltr',
                                                  textAlign: containsArabic(message.Message) ? 'right' : 'left'
                                                }}
                                              >
                                                {message.Message}
                                              </p>
                                            )}
                                          </div>
                                      }
                                    </div>
                                    <div className="conversation-name">
                                      <div className="d-flex align-items-center">
                                        <small className="text-muted time me-2">
                                          {new Date(message.CreatedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </small>

                                        {message.IsReceived && message.Platform ? <small className="badge text-bg-primary rounded-pill time me-2">
                                          {message.Platform}
                                        </small> : null}

                                        {/* Like/Dislike Status */}
                                        {message.IsReceived && likeStatus && message.IsLiked !== 0 && (
                                          <>
                                            <div className="d-flex align-items-center me-2 mr-2" id={`like-status-${message.ID}`}>
                                              <i className={`${likeStatus.icon} fs-15`} />
                                            </div>
                                            <UncontrolledTooltip target={`like-status-${message.ID}`}>{likeStatus.text}</UncontrolledTooltip>
                                          </>
                                        )}

                                        {/* Feedback Status Dropdown */}
                                        {/* {message.IsReceived && message.IsLiked === 2 && (
                                          <Dropdown
                                            // onMouseOver={() => toggleDropdown(message.ID)}
                                            // onMouseLeave={() => toggleDropdown(message.ID)}
                                            isOpen={dropdownOpen[message.ID]}
                                            toggle={() => toggleDropdown(message.ID)}
                                            direction="up" // Adjust direction as needed (up, down, left, right)
                                            className="ms-2"
                                          >
                                            <DropdownToggle tag="span" className="d-flex align-items-center cursor-pointer">
                                              {StatusLoading && updatingMessageId === message.ID ? (
                                                <>
                                                  <Spinner size="sm" className="me-2 mr-2" />
                                                  <span className="text-muted">{t('Updating')}</span>
                                                </>
                                              ) : (
                                                <>
                                                  <i className={`${statusDisplay.icon} fs-15 me-1 mr-1`} id={`status-icon-${message.ID}`} />
                                                  <span className="text-muted">{statusDisplay.text}</span>
                                                  <UncontrolledTooltip target={`status-icon-${message.ID}`}>{t('Change Status')}</UncontrolledTooltip>
                                                  <i className="ri-arrow-down-s-line ms-1 text-muted" />
                                                </>
                                              )}
                                            </DropdownToggle>
                                            <DropdownMenu> 
                                            <DropdownItem onClick={() => handleStatusChange(message.ID, 1)} active={message.Status === 1}>
                                              <i className="ri-time-line text-warning me-2" /> {t('Pending')}
                                            </DropdownItem>
                                            <DropdownItem onClick={() => handleStatusChange(message.ID, 2)} active={message.Status === 2}>
                                              <i className="ri-pause-line text-info me-2" /> {t('ChatReportLableOnHold')}
                                            </DropdownItem>
                                            <DropdownItem onClick={() => handleStatusChange(message.ID, 3)} active={message.Status === 3}>
                                              <i className="ri-check-double-line text-success me-2" /> {t('Completed')}
                                            </DropdownItem>
                                            <DropdownItem divider />
                                            <DropdownItem onClick={() => handleStatusChange(message.ID, 'No Feedback')} disabled={message.IsLiked === 0 && !message.feedbackStatus}>
                                                <i className="ri-feedback-line text-muted me-2" /> {t('No Feedback')}
                                            </DropdownItem>
                                          </DropdownMenu>
                                          </Dropdown>
                                        )} */}

                                        {/* Feedback Message */}
                                        {message.IsReceived && message.IsLiked === 2 && message.FeedbackMessage && (
                                          <>
                                            <div
                                              id={`feedback-msg-${message.ID}`}
                                              className="ms-2 bg-light text-dark rounded px-2 py-1"
                                              style={{
                                                maxWidth: '250px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'pointer'
                                              }}
                                            >
                                              "{message.FeedbackMessage}"
                                            </div><UncontrolledTooltip target={`feedback-msg-${message.ID}`} placement="top">
                                              {message.FeedbackMessage}
                                            </UncontrolledTooltip>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            )
                          })
                        }
                      </ul>
                    </PerfectScrollbar>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Modal>
  );
};

export default ChatHistoryAIChatModal;